import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /**
   * Gets all variants data.
   * @returns {Array<{id: number, option_value_ids: number[], available: boolean}>}
   */
  get variantsData() {
    const script = this.querySelector('script[data-all-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    this.#initFieldsets();

    this.addEventListener('change', this.variantChanged.bind(this));

    this.updateOptionsAvailability();
    this.updateSwatchValues();

    document.addEventListener(VariantUpdateEvent.eventName || ThemeEvents.variantUpdate, (event) => {
      const { detail } = /** @type {VariantUpdateEvent} */ (event);
      if (
        event.target !== this &&
        detail.data?.html &&
        detail.data?.productId === this.dataset.productId &&
        !detail.data?.newProduct
      ) {
        this.updateVariantPicker(detail.data.html);
      }
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    let selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const variants = this.variantsData;
    const selectedIds = this.selectedOptionsValues;
    const exactVariant = variants.find((v) =>
      selectedIds.every((id) => v.option_value_ids.map(String).includes(id))
    );
    let alternativeVariant = null;

    /*console.log('variants:', variants);
    console.log('selectedIds:', selectedIds);
    console.log('exactVariant:', exactVariant);
    console.log('selectedOptionsValues:', this.selectedOptionsValues);
    console.log('selectedOption:', selectedOption);*/

    if (variants.length > 0) {
      if (!exactVariant || !exactVariant.available) {
        const changedId = selectedOption.dataset.optionValueId;
        const possibleVariants = variants.filter(
          (v) => v.available && v.option_value_ids.map(String).includes(changedId)
        );

        if (possibleVariants.length > 0) {
          alternativeVariant = possibleVariants.reduce((best, current) => {
            const currentMatches = current.option_value_ids.filter((id) => selectedIds.includes(String(id))).length;
            const bestMatches = best.option_value_ids.filter((id) => selectedIds.includes(String(id))).length;
            return currentMatches > bestMatches ? current : best;
          });
        }

        if (!alternativeVariant && !selectedOption.dataset.connectedProductUrl) {
          alternativeVariant = variants.find((v) => v.available);
        }

        if (alternativeVariant) {
          alternativeVariant.option_value_ids.forEach((id) => {
            this.updateSelectedOption(id.toString());
          });

          if (alternativeVariant.option_value_ids.map(String).includes(changedId)) {
            const newSelectedOption = this.querySelector(`[data-option-value-id="${changedId}"]`);
            if (newSelectedOption instanceof HTMLElement) {
              selectedOption = newSelectedOption;
            }
          } else {
            const newSelectedOptions = Array.from(
              this.querySelectorAll('select option[selected], fieldset input:checked')
            );
            const bestOption =
              newSelectedOptions.find((opt) => opt instanceof HTMLElement && opt.dataset.connectedProductUrl) ||
              newSelectedOptions[0];

            if (bestOption instanceof HTMLElement) {
              selectedOption = bestOption;
            }
          }
        }
      }
    }

    this.updateSwatchValues();
    this.updateOptionsAvailability();

    const variantId = alternativeVariant
      ? alternativeVariant.id.toString()
      : exactVariant?.id.toString() || selectedOption.dataset.variantId || null;

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? '',
        variantId: variantId || '',
      })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, variantId), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) return;

      target = targetElement;
    }

    if (target instanceof HTMLOptionElement) {
      const select = target.closest('select');
      if (select) {
        select.value = target.value;
        target = select;
      }
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex] && newPreviousIndex !== newCurrentIndex) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [variantId] - The variant ID.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, variantId = null, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (variantId) {
      params.push(`variant=${variantId}`);
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`${this.tagName.toLowerCase()} script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    this.#initFieldsets();
    this.updateOptionsAvailability();
    this.updateSwatchValues();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Initializes fieldsets and radios.
   */
  #initFieldsets() {
    this.#radios = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, index) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      if (!this.#checkedIndices[index]) {
        const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
        this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
      }
    });
  }

  /**
   * Updates options availability.
   */
  updateOptionsAvailability() {
    const variants = this.variantsData;
    if (variants.length === 0) return;

    const selectedOptionsValues = this.selectedOptionsValues;
    const fieldsets = Array.from(this.querySelectorAll('fieldset, .variant-option--dropdowns'));

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      if (fieldset instanceof HTMLFieldSetElement) {
        const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        radios.forEach((radio) => {
          const optionValueId = radio.dataset.optionValueId;
          const isAvailable = this.#isOptionValueAvailable(optionValueId, fieldsetIndex, selectedOptionsValues, variants);

          radio.dataset.optionAvailable = isAvailable.toString();
          radio.setAttribute('aria-disabled', (!isAvailable).toString());
          this.#toggleStrikethrough(radio, isAvailable);
        });
      } else {
        const select = fieldset.querySelector('select');
        if (select) {
          Array.from(select.options).forEach((option) => {
            const optionValueId = option.dataset.optionValueId;
            const isAvailable = this.#isOptionValueAvailable(optionValueId, fieldsetIndex, selectedOptionsValues, variants);
            option.disabled = !isAvailable;
          });
        }
      }
    });
  }

  /**
   * Checks if an option value is available.
   * @param {string} optionValueId - The option value ID.
   * @param {number} fieldsetIndex - The fieldset index.
   * @param {string[]} selectedOptionsValues - The selected options values.
   * @param {Array} variants - The variants data.
   * @returns {boolean} True if available, false otherwise.
   */
  #isOptionValueAvailable(optionValueId, fieldsetIndex, selectedOptionsValues, variants) {
    return variants.some((variant) => {
      if (!variant.available) return false;

      const variantOptionValueIds = variant.option_value_ids.map(String);
      if (!variantOptionValueIds.includes(optionValueId)) return false;

      return selectedOptionsValues.every((selectedId, index) => {
        if (index === fieldsetIndex) return true;
        return variantOptionValueIds.includes(selectedId);
      });
    });
  }

  /**
   * Toggles the strikethrough SVG on a radio's label based on availability.
   * Matches the markup produced by `snippets/strikethrough-variant.liquid`.
   * @param {HTMLInputElement} radio
   * @param {boolean} isAvailable
   */
  #toggleStrikethrough(radio, isAvailable) {
    const label = radio.closest('label');
    if (!label) return;
    const existing = label.querySelector(':scope > svg');

    if (!isAvailable) {
      if (existing) return;
      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 100 46');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
      for (let i = 0; i < 2; i++) {
        const line = document.createElementNS(svgNS, 'line');
        line.setAttribute('x1', '100');
        line.setAttribute('y1', '0');
        line.setAttribute('x2', '0');
        line.setAttribute('y2', '46');
        line.setAttribute('vector-effect', 'non-scaling-stroke');
        svg.appendChild(line);
      }
      label.appendChild(svg);
    } else if (existing) {
      existing.remove();
    }
  }

  /**
   * Updates swatch values.
   */
  updateSwatchValues() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    fieldsets.forEach((fieldset) => {
      const swatchValue = fieldset.querySelector('.variant-option__swatch-value');
      if (swatchValue) {
        const checkedInput = fieldset.querySelector('input:checked');
        if (checkedInput) {
          swatchValue.textContent = checkedInput.value;
        }
      }
    });
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
